import {
  ExecutiveKpiTelemetry,
  AnalyticsEventDefinition,
  ProductFunnelModel,
  CohortRetentionMatrix,
  ChannelAttributionItem,
  AnalyticsExperimentItem,
  MarketOpportunityAnalyticsItem,
  AnalyticsPrivacyViolationItem,
  DeliverableP11
} from '../types';

export const INITIAL_EXECUTIVE_TELEMETRY: ExecutiveKpiTelemetry = {
  dauCount: 42850,
  wauCount: 198200,
  mauCount: 645000,
  stickinessDauMauPct: 6.64, // 42.85k / 645k
  successfulFinancialSessions30d: 512400,
  calculationCompletionRatePct: 78.4,
  timeToFirstUsefulResultSec: 14.2,
  organicClicks30d: 412000,
  registrationRatePct: 8.5,
  returningUserRate30dPct: 24.8,
  totalRevenue30dUsd: 48920.50,
  revenuePerQualifiedSessionUsd: 0.095,
  supportedMarketsCount: 22,
  unsupportedDemandEventsCount: 18450,
  activeIncidentsCount: 0,
  privacyScrubberViolationsBlockedCount: 12,
  ga4SyncHealthStatus: 'HEALTHY',
  firstPartyWarehouseStatus: 'OPERATIONAL',
};

export const INITIAL_EVENT_REGISTRY: AnalyticsEventDefinition[] = [
  {
    eventName: 'currency_conversion_completed',
    eventVersion: '1.0.0',
    domain: 'CURRENCY',
    description: 'Triggered when a user completes a live or historical currency pair calculation.',
    trigger: 'Conversion calculation form submission or instant update',
    owner: 'Currency Product Pod',
    privacyClass: 'PUBLIC_BEHAVIOR',
    allowedDestinations: ['FIRST_PARTY', 'GA4', 'INTERNAL_BI'],
    status: 'ACTIVE',
    properties: [
      { name: 'baseCurrency', type: 'string', description: 'ISO code of base currency', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'quoteCurrency', type: 'string', description: 'ISO code of target currency', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'rateType', type: 'string', description: 'LIVE, HISTORICAL, or CENTRAL_BANK', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'freshnessStatus', type: 'string', description: 'FRESH, STALE, or CACHED', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
    ],
  },
  {
    eventName: 'transfer_search_completed',
    eventVersion: '1.1.0',
    domain: 'TRANSFER',
    description: 'Triggered when money transfer corridor comparison engine returns provider quotes.',
    trigger: 'Corridor search executed',
    owner: 'Transfers Product Pod',
    privacyClass: 'PRODUCT_USAGE',
    allowedDestinations: ['FIRST_PARTY', 'GA4', 'INTERNAL_BI'],
    status: 'ACTIVE',
    properties: [
      { name: 'sendingCountry', type: 'string', description: 'Origin market ISO code', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'receivingCountry', type: 'string', description: 'Destination market ISO code', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'providerCount', type: 'number', description: 'Number of active quotes evaluated', privacyClass: 'PRODUCT_USAGE', destinations: ['FIRST_PARTY', 'GA4'], required: true },
    ],
  },
  {
    eventName: 'salary_calculation_completed',
    eventVersion: '1.2.0',
    domain: 'SALARY',
    description: 'Triggered when gross-to-net income tax calculation finishes successfully.',
    trigger: 'Salary calculation rendered with valid tax breakdown',
    owner: 'Salary & Tax Engine Pod',
    privacyClass: 'PRODUCT_USAGE',
    allowedDestinations: ['FIRST_PARTY', 'GA4', 'INTERNAL_BI'],
    status: 'ACTIVE',
    properties: [
      { name: 'country', type: 'string', description: 'Jurisdiction country ISO code', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'jurisdiction', type: 'string', description: 'State/Province code (e.g. CA, NY, ON)', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'calculationMode', type: 'string', description: 'quick vs detailed', privacyClass: 'PRODUCT_USAGE', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'payFrequency', type: 'string', description: 'annual, monthly, biweekly', privacyClass: 'PRODUCT_USAGE', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      // NOTICE: EXACT SALARY AMOUNT IS STRICTLY PROHIBITED FROM THIS REGISTRY!
    ],
  },
  {
    eventName: 'loan_calculation_completed',
    eventVersion: '1.0.0',
    domain: 'LOAN',
    description: 'Triggered when loan amortization or PITI calculation renders.',
    trigger: 'Loan calculator form submitted with valid interest & term inputs',
    owner: 'Mortgage & Loan Engine Pod',
    privacyClass: 'PRODUCT_USAGE',
    allowedDestinations: ['FIRST_PARTY', 'GA4', 'INTERNAL_BI'],
    status: 'ACTIVE',
    properties: [
      { name: 'market', type: 'string', description: 'Market code', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'loanType', type: 'string', description: 'MORTGAGE, AUTO, PERSONAL, REFINANCE', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'termBucket', type: 'string', description: '15_YEAR, 30_YEAR, 5_YEAR', privacyClass: 'PRODUCT_USAGE', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      // NOTICE: EXACT LOAN AMOUNT OR DEBT VALUE IS STRICTLY EXCLUDED!
    ],
  },
  {
    eventName: 'account_created',
    eventVersion: '1.0.0',
    domain: 'ACCOUNT',
    description: 'Triggered server-side when a new user registers via Passkey or Magic Link.',
    trigger: 'Server registration route success',
    owner: 'Identity & My YamooPay Pod',
    privacyClass: 'PERSONAL',
    allowedDestinations: ['FIRST_PARTY', 'GA4', 'INTERNAL_BI'],
    status: 'ACTIVE',
    properties: [
      { name: 'registrationMethod', type: 'string', description: 'PASSKEY or MAGIC_LINK', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
      { name: 'locale', type: 'string', description: 'User interface locale', privacyClass: 'PUBLIC_BEHAVIOR', destinations: ['FIRST_PARTY', 'GA4'], required: true },
    ],
  },
];

export const INITIAL_FUNNELS: ProductFunnelModel[] = [
  {
    id: 'fn_primary_product',
    funnelName: 'YamooPay Global End-to-End Product Funnel',
    toolDomain: 'PRIMARY',
    description: 'Global conversion funnel from organic acquisition to account registration and return visit.',
    overallConversionRatePct: 4.82,
    steps: [
      { stepNumber: 1, name: 'Landing Page Entrance', eventName: 'page_viewed', usersCount: 100000, conversionRatePct: 100.0, dropOffRatePct: 0.0, averageTimeMs: 0 },
      { stepNumber: 2, name: 'Financial Tool Opened', eventName: 'tool_opened', usersCount: 84200, conversionRatePct: 84.2, dropOffRatePct: 15.8, averageTimeMs: 2400 },
      { stepNumber: 3, name: 'Calculation Started', eventName: 'calculation_started', usersCount: 76500, conversionRatePct: 90.8, dropOffRatePct: 9.2, averageTimeMs: 4100 },
      { stepNumber: 4, name: 'Calculation Completed (Useful Result)', eventName: 'calculation_completed', usersCount: 68400, conversionRatePct: 89.4, dropOffRatePct: 10.6, averageTimeMs: 7700 },
      { stepNumber: 5, name: 'Result Engagement (Save/Favorite/Alert)', eventName: 'scenario_saved', usersCount: 18200, conversionRatePct: 26.6, dropOffRatePct: 73.4, averageTimeMs: 12400 },
      { stepNumber: 6, name: 'My YamooPay Account Creation', eventName: 'account_created', usersCount: 4820, conversionRatePct: 26.4, dropOffRatePct: 73.6, averageTimeMs: 28000 },
    ],
  },
  {
    id: 'fn_salary_engine',
    funnelName: 'Salary & Income Tax Calculation Funnel',
    toolDomain: 'SALARY',
    description: 'Specific conversion lifecycle for gross-to-net tax calculations.',
    overallConversionRatePct: 28.5,
    steps: [
      { stepNumber: 1, name: 'Salary Tool Opened', eventName: 'salary_calculator_opened', usersCount: 50000, conversionRatePct: 100.0, dropOffRatePct: 0.0, averageTimeMs: 0 },
      { stepNumber: 2, name: 'Jurisdiction Selected', eventName: 'jurisdiction_selected', usersCount: 46200, conversionRatePct: 92.4, dropOffRatePct: 7.6, averageTimeMs: 3200 },
      { stepNumber: 3, name: 'Tax Calculation Executed', eventName: 'salary_calculation_completed', usersCount: 41800, conversionRatePct: 90.4, dropOffRatePct: 9.6, averageTimeMs: 6500 },
      { stepNumber: 4, name: 'Detailed Deduction Breakdown Viewed', eventName: 'tax_breakdown_viewed', usersCount: 28400, conversionRatePct: 67.9, dropOffRatePct: 32.1, averageTimeMs: 14200 },
      { stepNumber: 5, name: 'Paycheck Scenario Saved', eventName: 'salary_scenario_saved', usersCount: 14250, conversionRatePct: 50.1, dropOffRatePct: 49.9, averageTimeMs: 22000 },
    ],
  },
];

export const INITIAL_RETENTION_MATRIX: CohortRetentionMatrix[] = [
  { cohortName: 'Aug 2026 Week 1', size: 28400, d1Pct: 34.2, d7Pct: 26.8, d30Pct: 21.4, w1Pct: 28.4, w4Pct: 22.1, m3Pct: 18.2, meaningfulCalculationsCount: 74200 },
  { cohortName: 'Aug 2026 Week 2', size: 31200, d1Pct: 35.8, d7Pct: 28.1, d30Pct: 22.9, w1Pct: 29.8, w4Pct: 23.5, m3Pct: 19.4, meaningfulCalculationsCount: 82500 },
  { cohortName: 'Aug 2026 Week 3', size: 34500, d1Pct: 37.4, d7Pct: 29.5, d30Pct: 24.8, w1Pct: 31.2, w4Pct: 25.1, m3Pct: 20.8, meaningfulCalculationsCount: 94100 },
];

export const INITIAL_CHANNEL_ATTRIBUTION: ChannelAttributionItem[] = [
  { channel: 'Organic Search (Google / Bing)', qualifiedVisitorsCount: 412000, successfulCalculationsCount: 328000, savedScenariosCount: 54200, signupsCount: 24100, affiliateClicksCount: 18400, grossRevenueUsd: 31200.50, firstTouchSharePct: 64.2, lastTouchSharePct: 62.5, lastNonDirectSharePct: 68.4 },
  { channel: 'Direct Traffic (Bookmarks / Brand)', qualifiedVisitorsCount: 124000, successfulCalculationsCount: 104000, savedScenariosCount: 28400, signupsCount: 14200, affiliateClicksCount: 8200, grossRevenueUsd: 9800.00, firstTouchSharePct: 18.5, lastTouchSharePct: 22.1, lastNonDirectSharePct: 14.2 },
  { channel: 'Referral (Financial Publishers)', qualifiedVisitorsCount: 48000, successfulCalculationsCount: 39500, savedScenariosCount: 8200, signupsCount: 4100, affiliateClicksCount: 4100, grossRevenueUsd: 5420.00, firstTouchSharePct: 11.2, lastTouchSharePct: 10.4, lastNonDirectSharePct: 12.1 },
  { channel: 'Email & Lifecycle Alerts', qualifiedVisitorsCount: 24000, successfulCalculationsCount: 22100, savedScenariosCount: 7800, signupsCount: 2400, affiliateClicksCount: 2800, grossRevenueUsd: 2500.00, firstTouchSharePct: 6.1, lastTouchSharePct: 5.0, lastNonDirectSharePct: 5.3 },
];

export const INITIAL_EXPERIMENTS: AnalyticsExperimentItem[] = [
  {
    id: 'exp_p11_01',
    experimentKey: 'exp_salary_form_field_order',
    title: 'Salary Calculator Field Reordering (Quick Mode vs Detailed Mode)',
    hypothesis: 'Placing State/Jurisdiction selector immediately below Country selector increases quick tax calculation completion rate by 8%.',
    owner: 'UX Analytics Scientist',
    domain: 'SALARY',
    variants: [
      { key: 'control', label: 'Control: Country -> Salary -> State', trafficAllocationPct: 50 },
      { key: 'treatment', label: 'Variant B: Country -> State -> Salary', trafficAllocationPct: 50 },
    ],
    primaryMetric: 'Calculation Completion Rate %',
    primaryMetricDeltaPct: +7.8,
    guardrails: [
      { metricName: 'Calculation Failure Rate', threshold: '<1.0%', currentValue: '0.12%', passed: true },
      { metricName: 'Page LCP Latency', threshold: '<1200ms', currentValue: '840ms', passed: true },
      { metricName: 'Tax Math Accuracy', threshold: '100% Deterministic', currentValue: '100.0%', passed: true },
    ],
    minSampleSize: 10000,
    currentSampleSize: 24800,
    status: 'PASSED',
    financialMathIsExperiment: false, // MANDATORY GUARANTEE!
    startedAt: '2026-08-12',
  },
];

export const INITIAL_MARKET_OPPORTUNITIES: MarketOpportunityAnalyticsItem[] = [
  {
    countryCode: 'IN',
    countryName: 'India (IN)',
    monthlyOrganicDemandEstimate: 85000,
    unsupportedSearchAttemptsCount: 14200,
    searchSuccessRatePct: 42.5,
    productReadinessScore: 78,
    monetizationUpsideUsdMonthly: 4800,
    status: 'HIGH_DEMAND_UNSUPPORTED',
    recommendedAction: 'Deploy Prompt 06 India Income Tax Pack (New Tax Regime 2026) to capture 14.2k monthly unserved searches.',
  },
  {
    countryCode: 'US',
    countryName: 'United States (US)',
    monthlyOrganicDemandEstimate: 340000,
    unsupportedSearchAttemptsCount: 420,
    searchSuccessRatePct: 98.4,
    productReadinessScore: 98,
    monetizationUpsideUsdMonthly: 28500,
    status: 'LIVE_SUPPORTED',
    recommendedAction: 'Fully operational across all 50 states + DC.',
  },
  {
    countryCode: 'BR',
    countryName: 'Brazil (BR)',
    monthlyOrganicDemandEstimate: 42000,
    unsupportedSearchAttemptsCount: 3850,
    searchSuccessRatePct: 35.0,
    productReadinessScore: 45,
    monetizationUpsideUsdMonthly: 2100,
    status: 'EMERGING',
    recommendedAction: 'Queue IRPF tax rate tables for Wave 5 rollout.',
  },
];

export const INITIAL_PRIVACY_VIOLATIONS: AnalyticsPrivacyViolationItem[] = [
  {
    id: 'vio_101',
    timestamp: '2026-08-24T11:14:02Z',
    eventName: 'salary_calculation_completed',
    prohibitedPropertyDetected: 'exactSalaryAmount ($87,432.00)',
    attemptedDestination: 'GA4',
    actionTaken: 'BLOCKED_AND_STRIPPED',
    incidentSeverity: 'HIGH',
  },
  {
    id: 'vio_102',
    timestamp: '2026-08-23T16:45:10Z',
    eventName: 'account_created',
    prohibitedPropertyDetected: 'userEmail (john.doe@example.com)',
    attemptedDestination: 'GA4',
    actionTaken: 'BLOCKED_AND_STRIPPED',
    incidentSeverity: 'CRITICAL',
  },
];

export const DELIVERABLES_P11: DeliverableP11[] = [
  {
    code: 'DEL-P11-01',
    titleEn: 'Event Taxonomy & Financial Data Privacy Governance',
    titleFr: 'Taxonomie des Événements & Gouvernance de Confidentialité des Données Financières',
    category: 'Event Taxonomy & Data Privacy Governance',
    summary: 'Strict rule: MEASURE PRODUCT BEHAVIOR — NOT PRIVATE FINANCIAL LIVES. Automated privacy scrubber blocking exact salary, debt, or PII from GA4 and third parties.',
    details: [
      'Typed Event Taxonomy enforcing object_action naming convention and strict versioning.',
      'Classification hierarchy: PUBLIC_BEHAVIOR, PRODUCT_USAGE, PSEUDONYMOUS, PERSONAL, FINANCIAL_SENSITIVE, SECURITY_RESTRICTED.',
      'Automated PII & Financial Scrubber intercepting prohibited fields (exact salary, loan amount, email) before outbound routing.',
      'Deny-by-default destination policy ensuring new properties are never automatically broadcast to external platforms.',
    ],
    architectureNotes: 'Zero raw financial data leakage. External marketing analytics receive only privacy-reviewed metadata.',
  },
  {
    code: 'DEL-P11-02',
    titleEn: 'First-Party Collection Engine & GA4 Consent Adapter',
    titleFr: 'Moteur de Collecte First-Party & Adaptateur de Consentement GA4',
    category: 'First-Party Collection & GA4 Router',
    summary: 'YamooPay system of record independent of GA4. Asynchronous non-blocking event collection with consent-aware routing.',
    details: [
      'YamooPay First-Party Analytics SDK with client & server envelope wrapping and event deduplication.',
      'Consent-aware router honoring TCF v2.2 & GPP choices without breaking core calculator functionality.',
      'GA4 Adapter handling acquisition, navigation, and marketing funnels without treating GA4 as a database.',
      'Fail-closed principle: Product calculations remain 100% operational even if external analytics endpoints fail.',
    ],
    architectureNotes: 'Application code interacts strictly with YamooPay Analytics SDK — no loose gtag() calls scatter.',
  },
  {
    code: 'DEL-P11-03',
    titleEn: 'Funnel, Cohort & Retention Intelligence Platform',
    titleFr: 'Plateforme d\'Intelligence d\'Entonnoirs, Cohortes & Rétention',
    category: 'Funnel, Cohort & Retention Intelligence',
    summary: 'Comprehensive user value metrics defining Activation (First Useful Result) and Retention based on meaningful financial decisions.',
    details: [
      'Multi-stage funnels for Global Product, Currency, Money Transfers, Salary & Tax, and Mortgage & Loans.',
      'Cohort retention matrices tracking D1, D7, D30, W1, W4, and M3 returning calculation patterns.',
      'Time-to-Value metric measuring average seconds from landing page arrival to first useful calculation result (14.2s target).',
      'DAU / WAU / MAU stickiness telemetry ignoring bots, crawlers, and ad-refresh requests.',
    ],
    architectureNotes: 'Measures meaningful return tool sessions rather than simple page reloads.',
  },
  {
    code: 'DEL-P11-04',
    titleEn: 'Attribution & Controlled Experimentation Engine',
    titleFr: 'Moteur d\'Attribution & d\'Expérimentation Contrôlée',
    category: 'Attribution & Experimentation Engine',
    summary: 'Multi-model attribution (First Touch, Last Touch, Last Non-Direct) and A/B testing with mathematical correctness guardrails.',
    details: [
      'Attribution engine connecting organic search, direct traffic, referrals, and affiliate partner conversions.',
      'Experimentation platform supporting sticky user variant assignments and guardrail metrics.',
      'STRICT GUARANTEE: Financial math and tax formulas are NEVER subject to A/B testing experiments.',
      'UTM parameter governance and campaign registry preventing random ad-hoc campaign tag pollution.',
    ],
    architectureNotes: 'Tax calculations remain 100% deterministic and audited regardless of UX A/B test variants.',
  },
  {
    code: 'DEL-P11-05',
    titleEn: 'Market Opportunity Analytics & Tool Scorecards',
    titleFr: 'Analytics d\'Opportunités de Marchés & Cartes de Score des Outils',
    category: 'Market Opportunity & Tool Scorecards',
    summary: 'Unserved demand intelligence identifying high-volume search queries for unsupported country packs (e.g., India Salary).',
    details: [
      'Unsupported Demand Engine capturing unserved search attempts to guide country pack expansion roadmap.',
      'Tool Performance Scorecards measuring Completion Rate, Return Rate, Save Rate, and Revenue per Qualified Session.',
      'Cross-tool journey analytics tracking natural user progression (Salary -> Affordability -> Loan).',
      'Search-to-Product funnel connecting Google Search Console observations with real calculator usage.',
    ],
    architectureNotes: 'Data-driven expansion prioritizing country packs based on real organic search volume.',
  },
  {
    code: 'DEL-P11-06',
    titleEn: 'Executive Command Center & Business Intelligence Dashboard',
    titleFr: 'Centre de Commandement Exécutif & Tableau de Bord de Business Intelligence',
    category: 'Executive Command Center & Business Intelligence',
    summary: 'Single-screen executive view providing total operational visibility: DAU/MAU, Revenue/Session, Supported Markets, and Critical Alerts.',
    details: [
      'Executive KPI Bar: DAU/MAU, Successful Financial Tool Sessions, Organic Clicks, Total Revenue, and Revenue/Session.',
      'Integrated drill-downs into Event Registry, Funnel Intelligence, Attribution/Experiments, and Data Quality.',
      'Real-time Data Quality & Privacy Scrubber monitor flagging schema violations or prohibited property attempts.',
      'Light-mode executive visual language adhering strictly to YamooPay Design System standards.',
    ],
    architectureNotes: 'Centralized decision cockpit unifying acquisition, product value, monetization, and privacy compliance.',
  },
];
